import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
    Building2, Calendar, MapPin, Users, Loader2, AlertTriangle, CheckCircle,
    UserPlus, Trash2, Save, Info,
} from 'lucide-react';
import { toast } from 'sonner';
import { inHouseApi } from '../../lib/api';

const eur = (n) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);

/**
 * Öffentliche Seite für die Firmen-Anmeldung. Route: /in-house/:token
 * Firma trägt Rechnungsanschrift ein und meldet Mitarbeiter an.
 */
export default function InHouseSignupPage() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [savingAddress, setSavingAddress] = useState(false);
    const [addingParticipant, setAddingParticipant] = useState(false);

    const [addr, setAddr] = useState({ name: '', street: '', zip: '', city: '', country: 'Deutschland', vat_id: '', contact_email: '' });
    const [fn, setFn] = useState('');
    const [ln, setLn] = useState('');

    const load = () => {
        setLoading(true);
        inHouseApi.get(token)
            .then(r => {
                setData(r.data);
                if (r.data.invoice_address) setAddr({ ...addr, ...r.data.invoice_address });
                setLoading(false);
            })
            .catch(e => {
                setError(e.response?.data?.detail || 'Link konnte nicht geladen werden');
                setLoading(false);
            });
    };

    useEffect(() => { if (token) load(); /* eslint-disable-next-line */ }, [token]);

    const saveAddress = async () => {
        if (!addr.name.trim() || !addr.street.trim() || !addr.zip.trim() || !addr.city.trim()) {
            toast.error('Bitte alle Pflichtfelder ausfüllen');
            return;
        }
        setSavingAddress(true);
        try {
            await inHouseApi.setInvoiceAddress(token, addr);
            toast.success('Rechnungsanschrift gespeichert');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
        } finally {
            setSavingAddress(false);
        }
    };

    const addParticipant = async () => {
        if (!fn.trim() || !ln.trim()) {
            toast.error('Bitte Vor- und Nachname angeben');
            return;
        }
        setAddingParticipant(true);
        try {
            await inHouseApi.addParticipant(token, { first_name: fn.trim(), last_name: ln.trim() });
            setFn(''); setLn('');
            toast.success('Teilnehmer hinzugefügt');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Teilnehmer konnte nicht hinzugefügt werden');
        } finally {
            setAddingParticipant(false);
        }
    };

    const removeParticipant = async (bookingId) => {
        try {
            await inHouseApi.removeParticipant(token, bookingId);
            toast.success('Teilnehmer entfernt');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Entfernen fehlgeschlagen');
        }
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center bg-muted/20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-muted/20 p-4">
                <Card className="max-w-md w-full" data-testid="ih-error-card">
                    <CardHeader><CardTitle className="flex items-center gap-2 text-red-700"><AlertTriangle className="h-5 w-5" /> Link nicht mehr gültig</CardTitle></CardHeader>
                    <CardContent className="text-sm space-y-3">
                        <p>{error}</p>
                        <p className="text-muted-foreground">Bitte kontaktieren Sie die BGPR-Akademie, falls Sie den Link erhalten haben, aber er nicht funktioniert.</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const s = data.seminar || {};
    const price = data.price || {};

    return (
        <div className="min-h-screen bg-muted/20 py-8 px-4">
            <div className="max-w-3xl mx-auto space-y-6" data-testid="in-house-signup-page">
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Building2 className="h-5 w-5 text-primary" />
                            Firmenfortbildung – Anmeldung
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm">
                        <div>
                            <div className="text-xs text-muted-foreground">Kurs</div>
                            <div className="font-semibold text-base">{s.title}</div>
                            {s.course_number && <div className="text-xs text-muted-foreground">Kurs-Nr.: {s.course_number}</div>}
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {s.date || '—'}</span>
                            <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {s.location || '—'}</span>
                            {s.time_start && <span>{s.time_start}{s.time_end ? ` – ${s.time_end}` : ''} Uhr</span>}
                        </div>
                        <div className="bg-primary/5 border border-primary/20 rounded-md p-3 text-sm">
                            <div className="font-semibold mb-0.5">Preis</div>
                            {price.type === 'flat'
                                ? <div>Pauschalpreis: <strong>{eur(price.flat_price)}</strong> (netto)</div>
                                : <div>{eur(price.per_participant_price)} pro Teilnehmer (netto)</div>}
                            <div className="text-xs text-muted-foreground mt-1">Sie erhalten eine Sammelrechnung nach dem Kurs.</div>
                        </div>
                    </CardContent>
                </Card>

                {/* Rechnungsanschrift */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                            Rechnungsanschrift {data.invoice_address && <CheckCircle className="h-4 w-4 text-emerald-600" />}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div className="grid gap-3 sm:grid-cols-2">
                            <div className="space-y-1.5 sm:col-span-2">
                                <Label>Firma / Name *</Label>
                                <Input value={addr.name} onChange={(e) => setAddr({ ...addr, name: e.target.value })} data-testid="ih-addr-name" />
                            </div>
                            <div className="space-y-1.5 sm:col-span-2">
                                <Label>Straße & Nr. *</Label>
                                <Input value={addr.street} onChange={(e) => setAddr({ ...addr, street: e.target.value })} data-testid="ih-addr-street" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>PLZ *</Label>
                                <Input value={addr.zip} onChange={(e) => setAddr({ ...addr, zip: e.target.value })} data-testid="ih-addr-zip" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Ort *</Label>
                                <Input value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} data-testid="ih-addr-city" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Land</Label>
                                <Input value={addr.country} onChange={(e) => setAddr({ ...addr, country: e.target.value })} />
                            </div>
                            <div className="space-y-1.5">
                                <Label>USt-IdNr. (optional)</Label>
                                <Input value={addr.vat_id || ''} onChange={(e) => setAddr({ ...addr, vat_id: e.target.value })} placeholder="DE123456789" />
                            </div>
                            <div className="space-y-1.5 sm:col-span-2">
                                <Label>E-Mail für Rechnungsversand (optional)</Label>
                                <Input type="email" value={addr.contact_email || ''} onChange={(e) => setAddr({ ...addr, contact_email: e.target.value })} />
                            </div>
                        </div>
                        <Button onClick={saveAddress} disabled={savingAddress} data-testid="ih-save-address">
                            {savingAddress ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
                            Anschrift speichern
                        </Button>
                    </CardContent>
                </Card>

                {/* Teilnehmer */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                            <Users className="h-4 w-4" />
                            Mitarbeiter ({data.participants_count} / {data.max_participants || '—'})
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        {data.max_participants && data.free_seats === 0 && (
                            <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md p-2 flex items-start gap-2">
                                <Info className="h-4 w-4 shrink-0 mt-0.5" />
                                Maximale Teilnehmerzahl erreicht.
                            </div>
                        )}
                        <div className="border rounded-lg divide-y" data-testid="ih-participants-list">
                            {(data.participants || []).length === 0 ? (
                                <div className="p-3 text-sm text-muted-foreground text-center">Noch keine Mitarbeiter eingetragen.</div>
                            ) : (
                                data.participants.map((p, idx) => (
                                    <div key={p.booking_id} className="p-2.5 flex items-center justify-between text-sm" data-testid={`ih-participant-${idx}`}>
                                        <div>
                                            <span className="font-medium">{p.first_name} {p.last_name}</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => removeParticipant(p.booking_id)}
                                            className="text-red-600 hover:text-red-800 p-1"
                                            aria-label="Entfernen"
                                            data-testid={`ih-remove-${idx}`}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>

                        <div className="grid gap-2 sm:grid-cols-2 pt-2 border-t">
                            <Input value={fn} onChange={(e) => setFn(e.target.value)} placeholder="Vorname" data-testid="ih-new-fn" />
                            <Input value={ln} onChange={(e) => setLn(e.target.value)} placeholder="Nachname" data-testid="ih-new-ln" />
                        </div>
                        <Button
                            onClick={addParticipant}
                            disabled={addingParticipant || (data.max_participants && data.free_seats === 0)}
                            className="w-full"
                            data-testid="ih-add-participant"
                        >
                            {addingParticipant ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <UserPlus className="h-4 w-4 mr-1.5" />}
                            Mitarbeiter hinzufügen
                        </Button>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
